/**
 * Monthly Parent Report (§22.17).
 *
 * Shows a compact preview of last month's headline numbers and lets the
 * parent download the full branding-clean PDF in one tap. The PDF is
 * rendered server-side; we pass child display names along (analytics
 * doesn't store the roster).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { analyticsApi } from '@/api/analytics';
import { usersApi } from '@/api/users';

export function MonthlyReportCard() {
  const report = useQuery({
    queryKey: ['monthly-report'],
    queryFn: () => analyticsApi.monthlyReport(),
    retry: false,
  });
  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
  });
  const [downloading, setDownloading] = useState(false);

  const r = report.data;
  if (!r) return null;

  const onDownload = async () => {
    setDownloading(true);
    try {
      const blob = await analyticsApi.monthlyReportPdf({
        year: r.year,
        month: r.month,
        children: (children.data ?? []).map((c) => ({ id: c.id, name: c.name })),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kidzio-report-${r.year}-${String(r.month).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not generate the report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const metrics: { value: number; label: string }[] = [
    { value: r.blocked_total, label: 'sites blocked' },
    { value: r.scam_phishing_blocked, label: 'scams stopped' },
    { value: r.geofence_events, label: 'check-ins' },
    { value: r.safety_alerts, label: 'safety alerts' },
  ];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <FileText className="size-4 text-[color:var(--kz-color-brand-primary)]" />
          <p className="flex-1 text-sm font-semibold">Monthly report — {r.month_label}</p>
          <Button size="sm" variant="outline" onClick={onDownload} disabled={downloading}>
            <Download className="mr-1 size-3.5" />
            {downloading ? 'Preparing…' : 'PDF'}
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-card bg-[color:var(--kz-color-surface-sunken)] p-2 text-center">
              <p className="text-lg font-semibold text-[color:var(--kz-color-brand-primary)]">{m.value}</p>
              <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">{m.label}</p>
            </div>
          ))}
        </div>
        {r.ai_recommendations[0] && (
          <p className="mt-3 text-xs text-[color:var(--kz-color-ink-secondary)]">
            <span className="font-medium">Suggestion:</span> {r.ai_recommendations[0]}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
